import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, CircularProgress, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import useCertificationForm from '../../hooks/useCertificationForm';
import { useQueryClient } from 'react-query';

const CertificationForm = ({ onClose, id, cvId, onSubmit }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            title: '',
            description: '',
            expiration_month: '',
            expiration_year: ''
        }
    });

    const { isLoading, error, data, saveCertification } = useCertificationForm(id);

    useEffect(() => {
        if (data) {
            // Map backend field names to form field names
            const mappedData = {
                title: data.title || '',
                description: data.description || '',
                expiration_month: data.expiration_month || '',
                expiration_year: data.expiration_year || ''
            };
            reset(mappedData);
        }
    }, [data, reset]);

    const handleFormSubmit = async (formData) => {
        try {
            // Map form field names to backend field names
            const backendData = {
                title: formData.title,
                description: formData.description,
                expiration_month: formData.expiration_month ? parseInt(formData.expiration_month) : null,
                expiration_year: formData.expiration_year ? parseInt(formData.expiration_year) : null
            };
            
            // If cvId is provided, add it to the data
            if (cvId) {
                backendData.cv = cvId;
            }
            
            // Use onSubmit prop if provided, otherwise use saveCertification
            if (onSubmit) {
                await onSubmit(backendData);
            } else {
                await saveCertification(backendData);
                // Invalidate and refetch queries - use null if cvId is undefined to match the query key
                await queryClient.invalidateQueries(['certifications', cvId || null]);
                await queryClient.refetchQueries(['certifications', cvId || null]);
                // Also invalidate all certification queries to be safe
                await queryClient.invalidateQueries(['certifications']);
            }
            
            // Reset form and close modal
            reset();
            onClose();
        } catch (err) {
            // Error is handled by the hook and displayed in the form
        }
    };

    const handleCancel = () => {
        // Reset form and close modal
        reset();
        onClose();
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    // Generate month options
    const monthOptions = Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        return { value: month, label: month };
    });

    // Generate year options (current year + 10 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 11 }, (_, i) => {
        const year = currentYear + i;
        return { value: year, label: year };
    });

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box className="space-y-4">
                {error && (
                    <Typography color="error" className="mb-4">
                        {error}
                    </Typography>
                )}

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Certificate Title"
                    {...register('title', { required: 'Certificate title is required' })}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Description"
                    multiline
                    rows={4}
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                />

                <Box display="flex" gap={2} className='mb-4'>
                    <FormControl fullWidth>
                        <InputLabel id="expiration-month-label">Expiration Month</InputLabel>
                        <Select
                            labelId="expiration-month-label"
                            id="expiration-month"
                            label="Expiration Month"
                            {...register('expiration_month')}
                        >
                            <MenuItem value="">None</MenuItem>
                            {monthOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="expiration-year-label">Expiration Year</InputLabel>
                        <Select
                            labelId="expiration-year-label"
                            id="expiration-year"
                            label="Expiration Year"
                            {...register('expiration_year')}
                        >
                            <MenuItem value="">None</MenuItem>
                            {yearOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {id ? 'Update' : 'Add'} Certification
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default CertificationForm; 